import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Search, Settings, Upload, Eye } from 'lucide-react';
import axios from 'axios';

const ICardSetting = () => {
  const [settings, setSettings] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingSetting, setEditingSetting] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    institute_name: '',
    institute_address: '',
    logo_url: '',
    card_template: 'default',
    background_color: '#667eea',
    text_color: '#ffffff',
    validity_years: 4
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/students/icard-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching I-Card settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingSetting) {
        await axios.put(`${API_URL}/api/students/icard-settings/${editingSetting.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('I-Card setting updated successfully!');
      } else {
        await axios.post(`${API_URL}/api/students/icard-settings`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('I-Card setting created successfully!');
      }
      fetchSettings();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving I-Card setting:', error);
      alert('Failed to save I-Card setting. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (setting) => {
    setEditingSetting(setting);
    setFormData({
      institute_name: setting.institute_name || '',
      institute_address: setting.institute_address || '',
      logo_url: setting.logo_url || '',
      card_template: setting.card_template || 'default',
      background_color: setting.background_color || '#667eea',
      text_color: setting.text_color || '#ffffff',
      validity_years: setting.validity_years || 4
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingSetting(null);
    setFormData({
      institute_name: '',
      institute_address: '',
      logo_url: '',
      card_template: 'default',
      background_color: '#667eea',
      text_color: '#ffffff',
      validity_years: 4
    });
  };

  const handlePreview = () => {
    const previewWindow = window.open('', '_blank', 'width=400,height=300');
    const previewContent = generatePreviewContent();
    previewWindow.document.write(previewContent);
    previewWindow.document.close();
  };

  const generatePreviewContent = () => {
    return `
    <!DOCTYPE html>
    <html>
    <head>
      <title>I-Card Preview</title>
      <style>
        body { 
          font-family: Arial, sans-serif; 
          margin: 20px;
          background: #f0f0f0;
        }
        .icard-preview {
          width: 3.375in;
          height: 2.125in;
          border: 2px solid #333;
          border-radius: 8px;
          padding: 10px;
          background: ${formData.background_color};
          color: ${formData.text_color};
          position: relative;
        }
        .icard-header {
          text-align: center;
          border-bottom: 1px solid rgba(255,255,255,0.3);
          padding-bottom: 5px;
          margin-bottom: 8px;
        }
        .institute-name { font-size: 12px; font-weight: bold; }
        .institute-address { font-size: 8px; margin-top: 2px; }
        .student-info { font-size: 10px; }
        .student-name { font-weight: bold; font-size: 11px; margin-bottom: 3px; }
        .admission-no { font-size: 9px; margin-bottom: 2px; }
        .course { font-size: 9px; }
        .photo-placeholder {
          width: 40px;
          height: 40px;
          border: 1px solid rgba(255,255,255,0.5);
          border-radius: 4px;
          float: right;
          background: rgba(255,255,255,0.1);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 8px;
        }
        .validity {
          position: absolute;
          bottom: 5px;
          right: 10px;
          font-size: 7px;
        }
      </style>
    </head>
    <body>
      <h3>I-Card Preview</h3>
      <div class="icard-preview">
        <div class="icard-header">
          <div class="institute-name">${formData.institute_name || 'Institute Name'}</div>
          <div class="institute-address">${formData.institute_address || 'Institute Address'}</div>
        </div>
        <div class="photo-placeholder">PHOTO</div>
        <div class="student-info">
          <div class="student-name">Sample Student Name</div>
          <div class="admission-no">Adm. No: ADM25001</div>
          <div class="course">Sample Course</div>
        </div>
        <div class="validity">Valid for ${formData.validity_years} years</div>
      </div>
    </body>
    </html>
    `;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">I-Card Settings</h1>
          <p className="text-sm text-gray-500 mt-1">Configure I-Card template and design settings</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handlePreview}
            className="flex items-center gap-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
          >
            <Eye className="h-4 w-4" />
            Preview
          </button>
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            disabled={loading}
          >
            <Plus className="h-4 w-4" />
            Add Setting
          </button>
        </div>
      </div>

      {/* Info Card */}
      <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
        <div className="flex items-start">
          <Settings className="h-5 w-5 text-blue-400 mt-0.5" />
          <div className="ml-3">
            <p className="text-sm text-blue-700">
              Configure the design and layout settings for student I-Cards. These settings will be applied when printing I-Cards.
              You can preview changes before saving them.
            </p>
          </div>
        </div>
      </div>

      {/* Current Settings */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Current Settings</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Institute Details</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Design</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Validity</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="4" className="px-6 py-8 text-center text-gray-500">Loading...</td>
                </tr>
              ) : settings.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-6 py-8 text-center text-gray-500">No I-Card settings configured</td>
                </tr>
              ) : (
                settings.map((setting) => (
                  <tr key={setting.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{setting.institute_name}</div>
                      <div className="text-sm text-gray-500">{setting.institute_address}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-6 h-6 rounded border" 
                          style={{ backgroundColor: setting.background_color }}
                        ></div>
                        <span className="text-sm text-gray-500">{setting.card_template}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">{setting.validity_years} years</td>
                    <td className="px-6 py-4 text-right text-sm font-medium">
                      <button
                        onClick={() => handleEdit(setting)}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-screen overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">
              {editingSetting ? 'Edit I-Card Setting' : 'Add I-Card Setting'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Institute Name *</label>
                <input
                  type="text"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={formData.institute_name}
                  onChange={(e) => setFormData({ ...formData, institute_name: e.target.value })}
                  placeholder="Your Institute Name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Institute Address *</label>
                <textarea
                  required
                  rows="2"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={formData.institute_address}
                  onChange={(e) => setFormData({ ...formData, institute_address: e.target.value })}
                  placeholder="Complete institute address"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Logo URL (Optional)</label>
                <input
                  type="url"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={formData.logo_url}
                  onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                  placeholder="https://example.com/logo.png"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Background Color</label>
                  <input
                    type="color"
                    className="w-full h-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={formData.background_color}
                    onChange={(e) => setFormData({ ...formData, background_color: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Text Color</label>
                  <input
                    type="color"
                    className="w-full h-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={formData.text_color}
                    onChange={(e) => setFormData({ ...formData, text_color: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Validity (Years)</label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={formData.validity_years}
                  onChange={(e) => setFormData({ ...formData, validity_years: parseInt(e.target.value) })}
                />
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-blue-300"
                  disabled={loading}
                >
                  {loading ? 'Saving...' : (editingSetting ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ICardSetting;